import { useEffect, useState } from 'react'
import * as fcl from '@blocto/fcl'
import { CANCEL_ALL_NFT_SALE } from 'flow/transactions/cancelAllNFTSale.tx'
import { PURCHASE_NFT } from 'flow/transactions/purchaseNFT.tx'
import {
  getCancelNFTSaleTransaction,
  getListNFTTransaction
} from 'flow/transactions/marketplaceTransactionSelector'

import { useTransactions } from 'providers/TransactionsProvider'
import { useWallets } from 'providers/WalletsProvider'
import { waitForSealed } from 'utils/flow'
import { MIGRATE_MARKETPLACE_LISTINGS_TX } from 'flow/transactions/migrateListings.tx'
import { SETUP_MARKETPLACE } from 'flow/transactions/setupMarketplace.tx'
import { useSnackbar } from 'hooks/useSnackbar'
import { getPinnedMomentIds } from 'utils/pinnedMoments'
import {
  buyListingEvm,
  cancelListingEvm,
  listForSaleEvm,
  MarketplaceApprovalError,
  MarketplaceApiError
} from 'services/crossmint.marketplace.service'
import { setupCrossmintMarketplaceWallet } from 'services/crossmint.service'
import { invalidateUsersNFTsCache } from 'hooks/useUsersNFTs'

export enum TransactionStatus {
  LOADING,
  IDLE,
  INFLIGHT,
  DONE,
  ERROR
}

export default function useMarketplaceListing() {
  const [status, setStatus] = useState<TransactionStatus>(
    TransactionStatus.IDLE
  )

  const [isMarketplaceSetup, setIsMarketplaceSetup] = useState<boolean>(true)
  const [transactionId, setTransactionId] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const { addTx } = useTransactions()
  const {
    userAuth,
    refreshWallets,
    connectedWalletAddress,
    usingCrossmint,
    usingMetaMask
  } = useWallets()
  const { openSnackbar } = useSnackbar()

  // Marketplace write actions (list/cancel/buy/changePrice) currently run via
  // Flow FCL with the Magic-provided `userAuth`. Crossmint users route through
  // the BE EVM endpoints (admin-signed on Base) — that branch is handled per
  // action below. MetaMask still has no marketplace path, so this short-circuit
  // remains for it.
  function ensureFlowMarketplaceAvailable(action: string): boolean {
    if (usingMetaMask || !userAuth) {
      const wallet = usingMetaMask ? 'MetaMask' : 'this wallet'
      openSnackbar(
        `${action} from ${wallet} is coming soon — marketplace on Base is in development.`,
        6000,
        'info'
      )
      setStatus(TransactionStatus.ERROR)
      setErrorMessage(`${action} is not yet available for ${wallet} wallets.`)
      return false
    }
    return true
  }

  // Turn a thrown error from the EVM marketplace service into a friendly
  // user-facing snackbar message + sets the hook's error state. Used by the
  // three EVM branches below to keep the catch blocks small. The approval/
  // allowance 403s should never fire for Crossmint wallets created after the
  // auto-approve backfill (Task #21), but legacy wallets may still hit them.
  function handleEvmError(action: string, e: unknown): void {
    setStatus(TransactionStatus.ERROR)
    if (e instanceof MarketplaceApprovalError) {
      const msg =
        'Your wallet is not yet marketplace-ready. Please contact support so we can finish setup.'
      setErrorMessage(msg)
      openSnackbar(msg, 6000, 'error')
      console.error(
        `[useMarketplaceListing] ${action} approval error`,
        e.detail
      )
      return
    }
    if (e instanceof MarketplaceApiError) {
      const msg =
        e.body?.error || `${action} failed (${e.status}). Please try again.`
      setErrorMessage(msg)
      openSnackbar(msg, 5000, 'error')
      return
    }
    const fallback = (e as Error)?.message || `${action} failed unexpectedly.`
    setErrorMessage(fallback)
    openSnackbar(fallback, 5000, 'error')
  }

  useEffect(() => {
    async function checkMarketplaceSetup() {
      try {
        const isSetup = await checkIsMarketplaceSetup(connectedWalletAddress)
        // console.log({ isSetup })
        setIsMarketplaceSetup(isSetup)
      } catch (e) {
        console.error(e)
        // add papertrail(e)
        setIsMarketplaceSetup(false)
      }
    }

    checkMarketplaceSetup()
  }, [connectedWalletAddress])

  async function checkIsMarketplaceSetup(userAddress: string) {
    if (!userAddress) return false
    const res = await fcl.query({
      cadence: `
        import NonFungibleToken from 0xNFT
        import AFLNFT from 0xAFLContracts
        import AFLMarketplaceV2 from 0xAFLContracts

        access(all) fun main(userAddress: Address): AnyStruct {
          let seller = getAccount(userAddress)
          return seller.capabilities.borrow<&AFLMarketplaceV2.SaleCollection>(/public/AFLMarketplaceSaleCollectionV2)
        }
        `,
      args: (arg: any, t: any) => [fcl.arg(userAddress, fcl.t.Address)]
    })
    // console.log('is setup check', res)
    return !!res
  }

  async function listNFT(nftId: string, salePrice: string) {
    if (!nftId || !salePrice) return

    // Check if moment is pinned (applies to both Flow + EVM paths)
    const pinnedMoments = getPinnedMomentIds()
    if (pinnedMoments.includes(nftId)) {
      openSnackbar(
        'Cannot list a pinned moment. Please unpin it first.',
        5000,
        'error'
      )
      setStatus(TransactionStatus.ERROR)
      setErrorMessage('Cannot list a pinned moment. Please unpin it first.')
      return
    }

    // ─── EVM branch (Crossmint custodial wallet) ──────────────────────────
    // Calls BE which admin-signs listForSale on Base. BE waits for receipt
    // (5–15s) before responding. priceUsdc is the raw 6-decimal USDC integer.
    if (usingCrossmint) {
      const priceDollars = parseFloat(salePrice.replace(/,/g, ''))
      if (!Number.isFinite(priceDollars) || priceDollars <= 0) {
        setStatus(TransactionStatus.ERROR)
        setErrorMessage('Invalid sale price.')
        return
      }
      const priceUsdc = Math.round(priceDollars * 1_000_000)
      try {
        setStatus(TransactionStatus.INFLIGHT)
        const result = await listForSaleEvm(parseInt(nftId, 10), priceUsdc)
        addTx(result.txHash, 'Listing for sale')
        setTransactionId(result.txHash)
        setStatus(TransactionStatus.DONE)
        refreshWallets()
        invalidateUsersNFTsCache(connectedWalletAddress)
      } catch (e) {
        // "Admin is not approved as operator" — auto-fix by calling
        // setup-marketplace-wallet, wait 30 s for the on-chain approval to
        // propagate, then retry the listing once.
        if (
          e instanceof MarketplaceApprovalError &&
          e.kind === 'nft-approval'
        ) {
          try {
            openSnackbar(
              'Setting up marketplace approval — please wait ~30 seconds…',
              35000,
              'info'
            )
            await setupCrossmintMarketplaceWallet()
            await new Promise((r) => setTimeout(r, 30000))
            setStatus(TransactionStatus.INFLIGHT)
            const retryResult = await listForSaleEvm(
              parseInt(nftId, 10),
              priceUsdc
            )
            addTx(retryResult.txHash, 'Listing for sale')
            setTransactionId(retryResult.txHash)
            setStatus(TransactionStatus.DONE)
            refreshWallets()
            invalidateUsersNFTsCache(connectedWalletAddress)
            openSnackbar(
              'Your moment has been listed for sale.',
              5000,
              'success'
            )
            return
          } catch (retryErr) {
            handleEvmError(
              'Listing for sale (after marketplace setup)',
              retryErr
            )
            throw retryErr
          }
        }
        handleEvmError('Listing for sale', e)
        throw e
      }
      return
    }

    if (!ensureFlowMarketplaceAvailable('Listing NFTs for sale')) return

    const price = salePrice.replace(/,/g, '')
    try {
      setStatus(TransactionStatus.INFLIGHT)
      const listNFTTransaction = await getListNFTTransaction()
      const txResult = await fcl.send([
        fcl.transaction(listNFTTransaction),
        fcl.args([
          fcl.arg(String(nftId), fcl.t.UInt64),
          fcl.arg(String(price), fcl.t.UFix64)
        ]),
        fcl.payer(userAuth),
        fcl.proposer(userAuth),
        fcl.authorizations([userAuth]),
        fcl.limit(9999)
      ])
      addTx(txResult.transactionId, 'Listing for sale')
      setTransactionId(txResult.transactionId)
      // await fcl.tx(txResult).onceSealed()
      await waitForSealed(txResult.transactionId)
      setStatus(TransactionStatus.DONE)
      refreshWallets()
    } catch (e) {
      setStatus(TransactionStatus.ERROR)
      console.error(e)
      const errorMessage = (e as Error).toString()
      if (errorMessage.includes('Team Badges cannot be listed for sale.')) {
        setErrorMessage('Team Badges cannot be listed for sale.')
      } else {
        setErrorMessage((e as Error).toString())
      }
      // add papertrail(e)
      throw e
    }
  }

  async function setupMarketplace() {
    if (!ensureFlowMarketplaceAvailable('Setting up the marketplace')) return
    try {
      setStatus(TransactionStatus.INFLIGHT)
      const transactionResult = await fcl.send([
        fcl.transaction(SETUP_MARKETPLACE),
        fcl.payer(userAuth),
        fcl.proposer(userAuth),
        fcl.authorizations([userAuth]),
        fcl.limit(9999)
      ])
      addTx(transactionResult.transactionId, 'Setting up marketplace')
      setTransactionId(transactionResult.transactionId)
      // await fcl.tx(transactionId).onceSealed()
      await waitForSealed(transactionResult.transactionId)
      setStatus(TransactionStatus.DONE)
    } catch (e) {
      console.error(e)
      // add papertrail(e)
      setErrorMessage((e as Error).toString())
      setStatus(TransactionStatus.ERROR)
      throw e
    }
  }

  async function cancelNFTSale(nftId: string) {
    if (!nftId) return

    // ─── EVM branch ───────────────────────────────────────────────────────
    // BE uses adminCancelListing so the admin signer can cancel without
    // needing seller == msg.sender. BE blocks ~5–15s for receipt.
    if (usingCrossmint) {
      try {
        setStatus(TransactionStatus.INFLIGHT)
        const result = await cancelListingEvm(parseInt(nftId, 10))
        addTx(result.txHash, 'Cancelling Listing')
        setTransactionId(result.txHash)
        setStatus(TransactionStatus.DONE)
        // Doesn't force an immediate refetch — just clears the cache entry
        // so whenever the next fetch happens (mount, refetch(), etc.) it
        // hits the network instead of serving pre-cancel cached data.
        invalidateUsersNFTsCache(connectedWalletAddress)
      } catch (e) {
        handleEvmError('Cancelling listing', e)
        throw e
      }
      return
    }

    if (!ensureFlowMarketplaceAvailable('Cancelling listings')) return
    try {
      setStatus(TransactionStatus.INFLIGHT)
      const cancelNFTSaleTransaction = await getCancelNFTSaleTransaction()
      const transactionResult = await fcl.send([
        fcl.transaction(cancelNFTSaleTransaction),
        fcl.args([fcl.arg(String(nftId), fcl.t.UInt64)]),
        fcl.payer(userAuth),
        fcl.proposer(userAuth),
        fcl.authorizations([userAuth]),
        fcl.limit(9999)
      ])
      addTx(transactionResult.transactionId, 'Cancelling Listing')
      setTransactionId(transactionResult.transactionId)
      // await fcl.tx(transactionId).onceSealed()
      await waitForSealed(transactionResult.transactionId)
      setStatus(TransactionStatus.DONE)
    } catch (e) {
      console.error(e)
      // add papertrail(e)
      setErrorMessage((e as Error).toString())
      setStatus(TransactionStatus.ERROR)
      throw e
    }
  }

  async function cancelAllNFTSale() {
    if (!ensureFlowMarketplaceAvailable('Cancelling all listings')) return
    try {
      setStatus(TransactionStatus.INFLIGHT)
      const transactionResult = await fcl.send([
        fcl.transaction(CANCEL_ALL_NFT_SALE),
        fcl.payer(userAuth),
        fcl.proposer(userAuth),
        fcl.authorizations([userAuth]),
        fcl.limit(9999)
      ])
      addTx(
        transactionResult.transactionId,
        'Delisting All Moments from Marketplace'
      )
      setTransactionId(transactionResult.transactionId)
      // await fcl.tx(transactionId).onceSealed()
      await waitForSealed(transactionResult.transactionId)
      setStatus(TransactionStatus.DONE)
    } catch (e) {
      console.error(e)
      // add papertrail(e)
      setErrorMessage((e as Error).toString())
      setStatus(TransactionStatus.ERROR)
      throw e
    }
  }

  async function migrateMarketplaceListings() {
    if (!ensureFlowMarketplaceAvailable('Migrating Flow-era listings')) return
    try {
      setStatus(TransactionStatus.INFLIGHT)
      const transactionResult = await fcl.send([
        fcl.transaction(MIGRATE_MARKETPLACE_LISTINGS_TX),
        fcl.payer(userAuth),
        fcl.proposer(userAuth),
        fcl.authorizations([userAuth]),
        fcl.limit(9999)
      ])
      addTx(transactionResult.transactionId, 'Migrating Listings')
      setTransactionId(transactionResult.transactionId)
      // await fcl.tx(transactionId).onceSealed()
      await waitForSealed(transactionResult.transactionId)
      setStatus(TransactionStatus.DONE)
      refreshWallets()
    } catch (e) {
      console.error(e)
      // add papertrail(e)
      setErrorMessage((e as Error).toString())
      setStatus(TransactionStatus.ERROR)
      throw e
    }
  }

  async function purchaseNFT(
    sellerAddress: string,
    nftId: number,
    purchaseAmount: string
  ) {
    if (!nftId) return

    // ─── EVM branch ───────────────────────────────────────────────────────
    // Price + seller are read by the BE from the on-chain listing — FE
    // only needs tokenId. BE admin-signs purchase() and waits ~5–15s for
    // the receipt. USDC allowance is auto-granted at wallet provisioning
    // (Task #21) — if it's still missing, the 403 is caught + surfaced.
    if (usingCrossmint) {
      try {
        setStatus(TransactionStatus.INFLIGHT)
        const result = await buyListingEvm(nftId)
        addTx(result.txHash, 'Purchasing in progress')
        setTransactionId(result.txHash)
        setStatus(TransactionStatus.DONE)
        // The buyer's own moments cache — the new NFT should show up in
        // their Collectibles once it's confirmed.
        invalidateUsersNFTsCache(connectedWalletAddress)
      } catch (e) {
        handleEvmError('Purchasing NFT', e)
        throw e
      }
      return
    }

    if (!sellerAddress || !purchaseAmount) return
    if (!ensureFlowMarketplaceAvailable('Purchasing NFTs')) return

    const isSellersMarketplaceSetup = await checkIsMarketplaceSetup(
      sellerAddress
    )
    if (!isSellersMarketplaceSetup) {
      openSnackbar(
        'Seller needs to update their account. Please try again later.',
        5000,
        'error'
      )
      return
    }

    try {
      setStatus(TransactionStatus.INFLIGHT)
      const tx = await fcl.send([
        fcl.transaction(PURCHASE_NFT),
        fcl.args([
          fcl.arg(sellerAddress, fcl.t.Address),
          fcl.arg(String(nftId), fcl.t.UInt64),
          fcl.arg(String(parseFloat(purchaseAmount).toFixed(2)), fcl.t.UFix64)
        ]),
        fcl.payer(userAuth),
        fcl.proposer(userAuth),
        fcl.authorizations([userAuth]),
        fcl.limit(9999)
      ])
      addTx(tx.transactionId, 'Purchasing in progress')
      setTransactionId(tx.transactionId)
      // await fcl.tx(tx).onceSealed()
      await waitForSealed(tx.transactionId)
      setStatus(TransactionStatus.DONE)
    } catch (e) {
      console.error(e)
      setStatus(TransactionStatus.ERROR)
      const errorMessage = (e as Error).message

      if (errorMessage.includes('No token matching this ID for sale!')) {
        setErrorMessage('This NFT is no longer available for sale')
        return
      } else {
        setErrorMessage(errorMessage)
      }

      throw e
    }
  }

  return {
    listNFT,
    cancelNFTSale,
    cancelAllNFTSale,
    purchaseNFT,
    migrateMarketplaceListings,
    setupMarketplace,
    isMarketplaceSetup,
    TransactionStatus,
    transactionId,
    status,
    errorMessage
  }
}
